import z from "zod";

export function downloadFile(filename: string, data: unknown): void {
  if (typeof data !== "string" && !(data instanceof Blob)) {
    throw new Error("Invalid data type");
  }
  const link = document.createElement("a");
  if (typeof data === "string") {
    link.href = data;
    if (isCrossOrigin(data)) {
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }
  } else {
    const objectUrl = window.URL.createObjectURL(data);
    link.href = objectUrl;
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
  }
  link.download = sanitizeFileName(filename);
  link.click();
}

const REVOKE_DELAY_MS = 60_000;

const MAX_FILE_NAME_LENGTH = 200;

// Path separators, control characters and characters Windows/macOS reject in a
// file name would silently mangle or truncate the saved file.
function sanitizeFileName(filename: string): string {
  const sanitized = filename
    .replace(/[\p{Cc}/\\?*:|"<>]/gu, "-")
    .replace(/-{2,}/g, "-")
    .trim()
    // Windows silently strips trailing dots and spaces, which would make the
    // saved name differ from the one we asked for.
    .replace(/[\s.]+$/, "");
  if (!sanitized) {
    return "download";
  }
  if (sanitized.length <= MAX_FILE_NAME_LENGTH) {
    return sanitized;
  }
  // Truncate the base name so the extension — which drives how the OS opens the
  // file — survives.
  const extension = /\.[\dA-Za-z]{1,10}$/.exec(sanitized)?.[0] ?? "";
  return (
    sanitized.slice(0, MAX_FILE_NAME_LENGTH - extension.length) + extension
  );
}

function isCrossOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return true;
  }
}

const utf8FilenameSchema = z
  .string()
  .regex(/filename\*\s*=\s*UTF-8''(.+)/i)
  .transform((val) => {
    const encoded = val.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1];
    if (!encoded) {
      return null;
    }
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  });

const asciiFilenameSchema = z
  .string()
  .regex(/filename\s*=\s*"([^"]+)"/i)
  .transform((val) => val.match(/filename\s*=\s*"([^"]+)"/i)?.[1] ?? null);

const filenameSchema = z.union([utf8FilenameSchema, asciiFilenameSchema]);

const headersWithFilenameSchema = z.object({
  "content-disposition": filenameSchema,
});

export function getFileNameFromHeaders(
  headers: unknown,
  defaultFileName: string,
): string {
  const result = headersWithFilenameSchema.safeParse(headers);
  if (!result.success) {
    return defaultFileName;
  }
  return result.data["content-disposition"] ?? defaultFileName;
}
